import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDraft } from '../contexts/DraftContext';
import { useAuth } from '../contexts/AuthContext';
import ScoringRulesEditor, { PRESETS, derivedPreset } from '../components/ScoringRulesEditor';
import OpponentPicker from '../components/OpponentPicker';
import AiButton from '../components/AiButton';
import { getAiErrorMessage } from '../utils/aiErrors';
import {
  getOpponents,
  createOpponent,
  linkOpponentToDraftSlot,
  getMockDraftUsage,
  getAggregatedRankings,
  getOpponentDraftProfile,
  getAiUsage,
} from '../services/api';

const TEAM_COUNTS = [8, 10, 12, 14];

const SEASON_TYPES = [
  { key: 'REDRAFT', label: 'Redraft' },
  { key: 'DYNASTY', label: 'Dynasty' },
];

const thStyle = { textAlign: 'left', padding: '4px 8px', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500, borderBottom: '1px solid var(--border)' };
const tdStyle = { padding: '6px 8px', verticalAlign: 'middle' };

const DEFAULT_ROSTER = { QB: 1, RB: 2, WR: 2, TE: 1, FLEX: 1, K: 1, DEF: 1, BN: 6 };
const ROSTER_POSITIONS = Object.keys(DEFAULT_ROSTER);

const SCORING_PRESETS = [
  { key: 'standard', label: 'Standard' },
  { key: 'half_ppr', label: '0.5 PPR' },
  { key: 'ppr',      label: 'PPR' },
];

/**
 * Form for configuring and starting a new snake draft.
 * Calls createDraft on submit; DraftContext transitions to the draft board.
 */
function DraftSetup() {
  const { createDraft } = useDraft();
  const { isPro } = useAuth();
  const [searchParams] = useSearchParams();
  // Reached via the dedicated "Mock Draft" button on the Draft Hub (already Pro-gated there) —
  // pre-flags this as a mock draft instead of relying on an easy-to-miss checkbox at the bottom.
  const isMockEntry = searchParams.get('mock') === '1';
  const [teamCount, setTeamCount] = useState(10);
  const [seasonType, setSeasonType] = useState('REDRAFT');
  // 'config' → main setup form; 'exclude-players' → pre-draft exclusion screen (dynasty only)
  const [step, setStep] = useState('config');
  const [excludedPlayers, setExcludedPlayers] = useState([]);
  const [exclusionPool, setExclusionPool] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [rosterSlots, setRosterSlots] = useState({ ...DEFAULT_ROSTER });
  const [draftName, setDraftName] = useState('');
  const [userPosition, setUserPosition] = useState(1);
  const [teamNames, setTeamNames] = useState(Array(10).fill(''));
  const [scoringRules, setScoringRules] = useState(PRESETS.half_ppr);
  const [showScoringEditor, setShowScoringEditor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [opponents, setOpponents] = useState([]);
  // Map of team index (0-based) -> opponentId string
  const [opponentLinks, setOpponentLinks] = useState({});
  const [mockDraftAvailable, setMockDraftAvailable] = useState(true);
  // O1 profile generation, surfaced inline per opponent row (requirements C5).
  const [aiUsageRemaining, setAiUsageRemaining] = useState(20);
  const [profileLoadingId, setProfileLoadingId] = useState(null);
  const [profileErrors, setProfileErrors] = useState({});
  // Only actually flagged as a mock draft when reached via the Mock Draft button (isMockEntry),
  // the user is Pro, and this week's mock draft hasn't already been used — otherwise it silently
  // falls back to creating a regular draft (isMockEntry-but-unavailable banner explains why above).
  const mockDraft = isMockEntry && isPro && mockDraftAvailable;

  useEffect(() => {
    getOpponents().then(setOpponents).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isPro) return;
    getMockDraftUsage().then(({ available }) => setMockDraftAvailable(available)).catch(() => {});
  }, [isPro]);

  useEffect(() => {
    if (!isPro) return;
    getAiUsage().then(data => setAiUsageRemaining(data.remaining)).catch(() => {});
  }, [isPro]);

  // Generates (or regenerates) an opponent's O1 draft profile from this row, updating only that
  // opponent's local status to CURRENT on success -- no full getOpponents() re-fetch (requirements C5).
  async function handleGenerateProfile(opponentId) {
    setProfileLoadingId(opponentId);
    setProfileErrors(prev => ({ ...prev, [opponentId]: null }));
    try {
      const result = await getOpponentDraftProfile(opponentId);
      setAiUsageRemaining(result.usageRemaining);
      setOpponents(prev => prev.map(o =>
        o.id === opponentId ? { ...o, profileStatus: 'CURRENT' } : o
      ));
    } catch (err) {
      setProfileErrors(prev => ({
        ...prev,
        [opponentId]: getAiErrorMessage(err, 'Could not generate a draft profile. Please try again.'),
      }));
    } finally {
      setProfileLoadingId(null);
    }
  }

  const rounds = Object.values(rosterSlots).reduce((a, b) => a + b, 0);

  function handleLink(teamIndex, opponentId) {
    setOpponentLinks(prev => ({ ...prev, [teamIndex]: opponentId }));
  }

  function handleUnlink(teamIndex) {
    setOpponentLinks(prev => {
      const next = { ...prev };
      delete next[teamIndex];
      return next;
    });
  }

  async function handleCreateAndLink(teamIndex, displayName) {
    try {
      const created = await createOpponent({ displayName });
      setOpponents(prev => [...prev, created]);
      setOpponentLinks(prev => ({ ...prev, [teamIndex]: created.id }));
    } catch {
      // silently ignore — user can retry
    }
  }

  function handleTeamCountChange(count) {
    setTeamCount(count);
    setUserPosition(pos => Math.min(pos, count));
    setTeamNames(prev => {
      const next = [...prev];
      while (next.length < count) next.push('');
      return next.slice(0, count);
    });
  }

  function handleTeamNameChange(i, value) {
    setTeamNames(prev => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  }

  function handleSlotChange(pos, delta) {
    setRosterSlots(prev => ({
      ...prev,
      [pos]: Math.max(0, Math.min(10, prev[pos] + delta)),
    }));
  }

  function handlePresetClick(key) {
    setScoringRules(PRESETS[key]);
    setShowScoringEditor(false);
  }

  // Config-step submit. Redraft submits immediately (no added friction — identical to before);
  // dynasty advances to the pre-draft exclusion screen instead, loading the player pool for the
  // chosen scoring format so the user can remove already-rostered players.
  async function handleStart(e) {
    e.preventDefault();
    if (seasonType === 'DYNASTY') {
      setStep('exclude-players');
      const format = derivedPreset(scoringRules);
      getAggregatedRankings(format).then(setExclusionPool).catch(() => setExclusionPool([]));
      return;
    }
    await submitDraft();
  }

  async function submitDraft() {
    setLoading(true);
    setError(null);
    try {
      const d = await createDraft({
        draftName: draftName.trim() || 'My Draft',
        teamCount,
        rounds,
        draftType: 'SNAKE',
        userTeamPosition: Number(userPosition),
        teamNames: teamNames.map((n, i) => n.trim() || `Team ${i + 1}`),
        sport: 'nfl',
        rosterConfig: rosterSlots,
        scoringFormat: derivedPreset(scoringRules),
        scoringRules,
        mockDraft,
        seasonType,
        excludedPlayers: seasonType === 'DYNASTY' ? excludedPlayers : [],
      });
      // Fire opponent links after draft is created (best-effort)
      // slotIndex matches teamIndex (both 0-based); skip the owner slot (it's set by the backend)
      await Promise.all(
        Object.entries(opponentLinks).map(([teamIndex, opponentId]) =>
          linkOpponentToDraftSlot(d.draftId, Number(teamIndex), opponentId).catch(() => {})
        )
      );
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to create draft');
    } finally {
      setLoading(false);
    }
  }

  function addExcludedPlayer(name) {
    setExcludedPlayers(prev => (prev.includes(name) ? prev : [...prev, name]));
  }

  function removeExcludedPlayer(name) {
    setExcludedPlayers(prev => prev.filter(n => n !== name));
  }

  const activePreset = derivedPreset(scoringRules);

  // Pre-draft player exclusion screen (dynasty only). Reached from the config step's "Continue"
  // button. One-off inline implementation of the search+chips pattern (see 4-6 decision); the
  // shared PlayerMultiSelect extraction was deferred as an optional refactor.
  if (step === 'exclude-players') {
    const q = playerSearch.trim().toLowerCase();
    const results = q
      ? exclusionPool
          .filter(p => p.playerName.toLowerCase().includes(q) && !excludedPlayers.includes(p.playerName))
          .slice(0, 15)
      : [];
    return (
      <div className='card' style={{ maxWidth: 640, margin: '0 auto' }}>
        <h2 style={{ marginTop: 0 }}>Exclude Rostered Players</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Remove players already on rosters from a prior season so they don&apos;t appear in this dynasty
          draft. If this is a first-year startup, just continue — nothing needs to be removed.
        </p>
        {error && <div className='error-banner'>{error}</div>}

        <div className='form-group'>
          <label>Search players</label>
          <input
            className='input'
            style={{ width: '100%' }}
            placeholder='Type a player name…'
            value={playerSearch}
            onChange={e => setPlayerSearch(e.target.value)}
          />
          {results.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 6 }}>
              {results.map(p => (
                <div key={p.playerName} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px' }}>
                  <span>
                    {p.playerName}{' '}
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                      {p.position}{p.team ? ` · ${p.team}` : ''}
                    </span>
                  </span>
                  <button type='button' className='filter-pill' onClick={() => addExcludedPlayer(p.playerName)}>
                    Add
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='form-group'>
          <label>Excluded ({excludedPlayers.length})</label>
          {excludedPlayers.length === 0 ? (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              No players excluded — treated as a year-1 startup.
            </span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {excludedPlayers.map(name => (
                <span key={name} className='filter-pill active' style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {name}
                  <button
                    type='button'
                    onClick={() => removeExcludedPlayer(name)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                    aria-label={`Remove ${name}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className='modal-actions' style={{ display: 'flex', gap: 8 }}>
          <button type='button' className='btn' onClick={() => setStep('config')} disabled={loading}>
            Back
          </button>
          <button type='button' className='btn btn-primary' onClick={submitDraft} disabled={loading}>
            {loading ? 'Creating...' : (excludedPlayers.length === 0 ? 'Skip — Start Draft' : 'Start Draft')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='card' style={{ maxWidth: 640, margin: '0 auto' }}>
      <h2 style={{ marginTop: 0 }}>{mockDraft ? '✦ Mock Draft' : 'New Draft'}</h2>
      {mockDraft && (
        <p className='mock-draft-banner'>
          AI simulates your opponents&apos; picks — one mock draft per week (Pro)
        </p>
      )}
      {isMockEntry && !isPro && (
        <div className='error-banner'>Mock Draft is a Pro feature — upgrade to unlock it.</div>
      )}
      {isMockEntry && isPro && !mockDraftAvailable && (
        <div className='error-banner'>
          Already used this week&apos;s mock draft — resets Monday at midnight UTC. This draft will be created as a regular draft.
        </div>
      )}
      {error && <div className='error-banner'>{error}</div>}
      <form onSubmit={handleStart}>
        <div className='form-group'>
          <label>Draft Name</label>
          <input
            className='input'
            style={{ width: '100%' }}
            placeholder='My Draft'
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div className='form-group'>
            <label>Number of Teams</label>
            <div className='filter-group'>
              {TEAM_COUNTS.map(n => (
                <button
                  type='button'
                  key={n}
                  className={`filter-pill ${teamCount === n ? 'active' : ''}`}
                  onClick={() => handleTeamCountChange(n)}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className='form-group'>
            <label>Your Pick Position</label>
            <select
              className='input'
              value={userPosition}
              onChange={e => setUserPosition(Number(e.target.value))}
              style={{ width: 120 }}
            >
              {Array.from({ length: teamCount }, (_, i) => (
                <option key={i + 1} value={i + 1}>Pick #{i + 1}</option>
              ))}
            </select>
          </div>
        </div>

        <div className='form-group'>
          <label>
            Roster Slots{' '}
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
              — {rounds} rounds total
            </span>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px 16px', marginTop: 8 }}>
            {ROSTER_POSITIONS.map(pos => (
              <div key={pos} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 36, fontSize: '0.85rem', fontWeight: 500 }}>{pos}</span>
                <button
                  type='button'
                  className='filter-pill'
                  style={{ padding: '2px 10px', minWidth: 28 }}
                  onClick={() => handleSlotChange(pos, -1)}
                  disabled={rosterSlots[pos] === 0}
                >
                  −
                </button>
                <span style={{ width: 20, textAlign: 'center' }}>{rosterSlots[pos]}</span>
                <button
                  type='button'
                  className='filter-pill'
                  style={{ padding: '2px 10px', minWidth: 28 }}
                  onClick={() => handleSlotChange(pos, 1)}
                  disabled={rosterSlots[pos] === 10}
                >
                  +
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className='form-group'>
          <label>Scoring</label>
          <div className='filter-group' style={{ flexWrap: 'wrap', gap: 6 }}>
            {SCORING_PRESETS.map(({ key, label }) => (
              <button
                type='button'
                key={key}
                className={`filter-pill ${activePreset === key ? 'active' : ''}`}
                onClick={() => handlePresetClick(key)}
              >
                {label}
              </button>
            ))}
            {activePreset === 'custom' && (
              <span className='filter-pill active'>Custom</span>
            )}
            <button
              type='button'
              className='btn-link'
              style={{ marginLeft: 4 }}
              onClick={() => setShowScoringEditor(prev => !prev)}
            >
              {showScoringEditor ? '▴ Collapse' : '▾ Customize'}
            </button>
          </div>
          {showScoringEditor && (
            <div style={{ marginTop: 12 }}>
              <ScoringRulesEditor rules={scoringRules} onChange={setScoringRules} />
            </div>
          )}
        </div>

        <div className='form-group'>
          <label>Season Type</label>
          <div className='filter-group'>
            {SEASON_TYPES.map(({ key, label }) => (
              <button
                type='button'
                key={key}
                className={`filter-pill ${seasonType === key ? 'active' : ''}`}
                onClick={() => setSeasonType(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {seasonType === 'DYNASTY' && (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              You can remove already-rostered players on the next screen.
            </span>
          )}
        </div>

        <div className='form-group'>
          <label>Team Names <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Team Name</th>
                <th style={thStyle}>Manager</th>
                <th style={thStyle}>AI Profile</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: teamCount }, (_, i) => {
                const isUserRow = i + 1 === userPosition;
                const linkedId = opponentLinks[i];
                const linkedOpponent = linkedId ? opponents.find(o => o.id === linkedId) || null : null;
                return (
                  <tr key={i}>
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={tdStyle}>
                      {isUserRow ? (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>You</span>
                      ) : (
                        <input
                          className='input'
                          style={{ width: '100%' }}
                          placeholder={`Team ${i + 1}`}
                          value={teamNames[i] || ''}
                          onChange={e => handleTeamNameChange(i, e.target.value)}
                        />
                      )}
                    </td>
                    <td style={tdStyle}>
                      {isUserRow ? (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>—</span>
                      ) : (
                        <OpponentPicker
                          currentOpponent={linkedOpponent}
                          opponents={opponents}
                          onLink={id => handleLink(i, id)}
                          onUnlink={() => handleUnlink(i)}
                          onCreateAndLink={name => handleCreateAndLink(i, name)}
                        />
                      )}
                    </td>
                    <td style={tdStyle}>
                      {!isUserRow && linkedOpponent && (
                        <>
                          {linkedOpponent.profileStatus === 'CURRENT' ? (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>✓ Profile ready</span>
                          ) : (
                            <AiButton
                              label={linkedOpponent.profileStatus === 'STALE' ? 'Regenerate Profile' : 'Generate Profile'}
                              isPro={isPro}
                              usageRemaining={isPro ? aiUsageRemaining : 0}
                              freeTooltip='Upgrade to Pro for AI-generated opponent draft profiles'
                              onClick={() => handleGenerateProfile(linkedOpponent.id)}
                              loading={profileLoadingId === linkedOpponent.id}
                              disabled={linkedOpponent.draftCount === 0}
                              disabledTooltip='No real draft history yet for this manager'
                            />
                          )}
                          {linkedOpponent.profileStatus === 'STALE' && (
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 2 }}>
                              Profile is out of date
                            </div>
                          )}
                          {profileErrors[linkedOpponent.id] && (
                            <div className='error-banner' style={{ marginTop: 4, fontSize: '0.75rem' }}>
                              {profileErrors[linkedOpponent.id]}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className='modal-actions'>
          <button className='btn btn-primary' type='submit' disabled={loading || rounds === 0}>
            {loading ? 'Creating...' : (seasonType === 'DYNASTY' ? 'Continue' : mockDraft ? 'Start Mock Draft' : 'Start Draft')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DraftSetup;
